import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { getAppointments, getDashboardStats, getDrafts, getFollowUpSuggestions, getPatients, getProfile } from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import DashboardWelcomeHero from "../components/dashboard-visual/DashboardWelcomeHero";
import DashboardQuickActions from "../components/dashboard-visual/DashboardQuickActions";
import TodayAgendaCard from "../components/dashboard-visual/TodayAgendaCard";
import PendingFollowUpsCard from "../components/dashboard-visual/PendingFollowUpsCard";
import DashboardAppearanceDialog from "../components/dashboard-visual/DashboardAppearanceDialog";
import PatientRecordLink from "../components/PatientRecordLink";
import {
  loadAppearance,
  personalizedGreeting,
  resolveHeroVisual,
  saveAppearance,
} from "../lib/dashboardAppearance";
import { patientRecordLabel, resolvePatientRecordId } from "../lib/patientRecordId";
import {
  formatAppointmentTime,
  getLocalDayRangeUtc,
  resolveBrowserTimezone,
  todayLocalDateString,
} from "../lib/agendaDateTime";
import { getTypeLabel, isPersonalBlock } from "../lib/appointmentStatus";
import { Activity } from "lucide-react";

function mapAppointmentForCard(apt, patientMap) {
  const personal = isPersonalBlock(apt.appointment_type);
  const patient = apt.patient_id ? patientMap[apt.patient_id] : null;
  return {
    id: apt.id,
    paciente_nombre: personal
      ? "Bloqueo personal"
      : patient?.label || "Paciente",
    hora: formatAppointmentTime(apt.start_at),
    motivo: apt.reason_short || getTypeLabel(apt.appointment_type),
  };
}
const SHOWCASE_PROFILE = { nombre_completo: "Aurora Demo", titulo: "Dra." };
const SHOWCASE_STATS = {
  today_appointments: [
    { id: "sample-apt-1", paciente_nombre: "M. R.", motivo: "Control general", hora: "08:30" },
    { id: "sample-apt-2", paciente_nombre: "J. L.", motivo: "Revisión funcional", hora: "10:15" },
    { id: "sample-apt-3", paciente_nombre: "A. P.", motivo: "Seguimiento cognitivo", hora: "16:00" },
  ],
  upcoming_appointments: [
    { id: "sample-up-1", paciente_nombre: "M. R.", motivo: "Próxima visita domiciliar", hora: "Mañana 09:00" },
  ],
  recent_visits: [
    { id: "sample-r-1", patient_name: "M. R.", visit_type: "Control funcional" },
    { id: "sample-r-2", patient_name: "J. L.", visit_type: "Ajuste terapéutico" },
  ],
};
const SHOWCASE_FOLLOWUPS = [
  { id: "sample-f-1", patient_name: "M. R.", type: "Reconsulta en 7 días" },
  { id: "sample-f-2", patient_name: "A. P.", type: "Control nutricional en 14 días" },
];
const SHOWCASE_DRAFTS = [
  { id: "sample-d-1", paciente_id: "", paciente_nombre: "Borrador · J. L." },
];

export default function DashboardVisualV1({ showcaseMode = false }) {
  const { user } = useAuth();
  const now = useMemo(() => new Date(), []);
  const [appearance, setAppearance] = useState(() => loadAppearance());
  const [appearanceOpen, setAppearanceOpen] = useState(false);
  const [profile, setProfile] = useState(showcaseMode ? SHOWCASE_PROFILE : null);
  const [stats, setStats] = useState(null);
  const [followUps, setFollowUps] = useState([]);
  const [drafts, setDrafts] = useState([]);
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(!showcaseMode);
  const [agendaToday, setAgendaToday] = useState([]);
  const [agendaLoading, setAgendaLoading] = useState(!showcaseMode);
  const [agendaUnavailable, setAgendaUnavailable] = useState(false);

  const visual = resolveHeroVisual(appearance, now);
  const greeting = showcaseMode
    ? "Buenos días, Dra. Aurora Demo"
    : personalizedGreeting(now, user, profile);

  useEffect(() => {
    if (showcaseMode) {
      setStats(SHOWCASE_STATS);
      setFollowUps(SHOWCASE_FOLLOWUPS);
      setDrafts(SHOWCASE_DRAFTS);
      setRecent(SHOWCASE_STATS.recent_visits);
      setAgendaToday(SHOWCASE_STATS.today_appointments);
      setAgendaLoading(false);
      setAgendaUnavailable(false);
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    const load = async () => {
      try {
        const [profileRes, statsRes, followRes, draftsRes] = await Promise.allSettled([
          getProfile(),
          getDashboardStats(),
          getFollowUpSuggestions(),
          getDrafts(),
        ]);
        if (cancelled) return;
        if (profileRes.status === "fulfilled") setProfile(profileRes.value?.data || null);
        if (statsRes.status === "fulfilled") {
          const data = statsRes.value?.data || {};
          setStats(data);
          setRecent(Array.isArray(data.recent_visits) ? data.recent_visits : []);
        }
        if (followRes.status === "fulfilled") {
          const payload = followRes.value?.data;
          setFollowUps(Array.isArray(payload?.suggestions) ? payload.suggestions : []);
        }
        if (draftsRes.status === "fulfilled") {
          const list = draftsRes.value?.data;
          setDrafts(Array.isArray(list) ? list : []);
        }
      } catch {
        if (!cancelled) toast.error("No se pudo cargar el panel visual.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [showcaseMode]);

  useEffect(() => {
    if (showcaseMode) return undefined;
    let cancelled = false;
    const loadAgenda = async () => {
      setAgendaLoading(true);
      try {
        const tz = resolveBrowserTimezone();
        const day = todayLocalDateString(tz);
        const range = getLocalDayRangeUtc(day, tz);
        const [apptRes, patientsRes] = await Promise.allSettled([
          getAppointments({ start: range.start, end: range.end, limit: 50 }),
          getPatients("all"),
        ]);
        if (cancelled) return;
        if (apptRes.status !== "fulfilled") {
          const status = apptRes.reason?.response?.status;
          if (status === 404 || status === 405 || status === 501) {
            setAgendaUnavailable(true);
            setAgendaToday([]);
          } else {
            setAgendaUnavailable(false);
            setAgendaToday([]);
          }
          return;
        }
        setAgendaUnavailable(false);
        const list = Array.isArray(apptRes.value?.data) ? apptRes.value.data : [];
        list.sort((a, b) => String(a.start_at).localeCompare(String(b.start_at)));
        const patientMap = {};
        if (patientsRes.status === "fulfilled") {
          const patients = Array.isArray(patientsRes.value?.data) ? patientsRes.value.data : [];
          patients.forEach((p) => {
            const id = resolvePatientRecordId(p);
            if (id) patientMap[id] = { label: patientRecordLabel(p) };
          });
        }
        setAgendaToday(list.map((apt) => mapAppointmentForCard(apt, patientMap)));
      } catch {
        if (!cancelled) {
          setAgendaUnavailable(false);
          setAgendaToday([]);
        }
      } finally {
        if (!cancelled) setAgendaLoading(false);
      }
    };
    loadAgenda();
    return () => {
      cancelled = true;
    };
  }, [showcaseMode]);

  const handleAppearanceChange = (next) => {
    const saved = saveAppearance(next);
    setAppearance(saved);
  };

  return (
    <div className="mx-auto w-full max-w-[1400px] space-y-6 pb-8" data-testid="dashboard-visual-v1">
      <DashboardWelcomeHero
        greeting={greeting}
        date={now}
        visual={visual}
        onOpenAppearance={() => setAppearanceOpen(true)}
        showcaseMode={showcaseMode}
      />
      <DashboardAppearanceDialog
        open={appearanceOpen}
        onOpenChange={setAppearanceOpen}
        appearance={appearance}
        onChange={handleAppearanceChange}
      />

      {showcaseMode ? (
        <div
          className="inline-flex items-center rounded-full border border-sky-200 bg-sky-50 px-3 py-1 text-xs font-medium text-sky-800"
          data-testid="showcase-badge"
        >
          Vista de diseño · Datos de ejemplo
        </div>
      ) : null}

      <DashboardQuickActions showcaseMode={showcaseMode} />

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" aria-busy="true">
          <div className="h-48 rounded-2xl bg-slate-100 animate-pulse motion-reduce:animate-none" />
          <div className="h-48 rounded-2xl bg-slate-100 animate-pulse motion-reduce:animate-none" />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 md:items-start gap-4">
          <TodayAgendaCard
            today={showcaseMode ? stats?.today_appointments : agendaToday}
            upcoming={showcaseMode ? stats?.upcoming_appointments : []}
            showcaseMode={showcaseMode}
            loading={!showcaseMode && agendaLoading}
            unavailable={!showcaseMode && agendaUnavailable}
            unavailableMessage="La Agenda está temporalmente no disponible."
          />
          <PendingFollowUpsCard suggestions={followUps} drafts={drafts} showcaseMode={showcaseMode} />
        </div>
      )}

      <Card className="border-slate-200 shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-lg text-slate-900">
            <Activity className="h-5 w-5 text-sky-700" />
            Actividad reciente
          </CardTitle>
        </CardHeader>
        <CardContent>
          {recent.length === 0 ? (
            <p className="text-sm text-slate-600">Aún no hay consultas recientes para mostrar.</p>
          ) : (
            <ul className="space-y-2">
              {recent.slice(0, 6).map((visit, index) => (
                <li key={visit.id || `visit-${index}`}>
                  {showcaseMode ? (
                    <button
                      type="button"
                      className="block w-full text-left rounded-lg border border-slate-100 bg-white px-3 py-2 hover:border-sky-200"
                      onClick={() => toast.info("Disponible en la plataforma autenticada.")}
                    >
                      <p className="text-sm font-medium text-slate-900">{patientRecordLabel(visit)}</p>
                      <p className="text-xs text-slate-500">
                        {visit.visit_type || visit.motivo || "Consulta"}
                      </p>
                    </button>
                  ) : (
                    <PatientRecordLink
                      record={visit}
                      className="block rounded-lg border border-slate-100 bg-white px-3 py-2 hover:border-sky-200"
                    >
                      <p className="text-sm font-medium text-slate-900">{patientRecordLabel(visit)}</p>
                      <p className="text-xs text-slate-500">
                        {visit.visit_type || visit.motivo || "Consulta"}
                      </p>
                    </PatientRecordLink>
                  )}
                </li>
              ))}
            </ul>
          )}
          <p className="mt-4 text-xs text-slate-500">
            {showcaseMode
              ? "Estos datos son ficticios y se muestran únicamente para evaluar el diseño."
              : "Esta carátula es una demostración visual. El panel actual sigue disponible."}{" "}
            <Link to={showcaseMode ? "/" : "/dashboard"} className="underline text-sky-800">
              {showcaseMode ? "Volver al inicio" : "Volver al panel actual"}
            </Link>
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
